import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LayoutDashboard, Package, PlusCircle, LogOut, Layers, Bell, Users, ClipboardList, UserCog, Percent, MapPin, TrendingUp } from "lucide-react";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reminderCount, setReminderCount] = useState(0);
  const role = user?.role || "viewer";
  const canEdit = role !== "viewer";
  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
    { to: "/orders", label: "Orders", icon: Package, id: "nav-orders" },
    ...(canEdit ? [{ to: "/orders/new", label: "New Order", icon: PlusCircle, id: "nav-new-order" }] : []),
    { to: "/reminders", label: "Reminders", icon: Bell, id: "nav-reminders", badge: true },
    { to: "/status", label: "Status", icon: ClipboardList, id: "nav-status" },
    { to: "/parties", label: "Parties", icon: Users, id: "nav-parties" },
    { to: "/commission-rates", label: "Commission", icon: Percent, id: "nav-commission" },
    { to: "/buyers", label: "Buyers", icon: MapPin, id: "nav-buyers" },
    ...(role === "admin" || user?.analytics_access ? [{ to: "/admin/analytics/fabric-demand", label: "Demand Intel", icon: TrendingUp, id: "nav-analytics" }] : []),
    ...(role === "admin" ? [{ to: "/users", label: "Users", icon: UserCog, id: "nav-users" }] : []),
  ];

  useEffect(() => {
    api.get("/reminders").then((r) => setReminderCount(r.data.length)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-[#F6F5F2]">
      <aside className="w-64 shrink-0 bg-white border-r border-[#E6E4DD] flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-[#E6E4DD]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1C2331] flex items-center justify-center rounded-sm">
              <Layers className="w-4 h-4 text-[#D95D39]" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-[#0A0D14] leading-none tracking-tight">Madhusudan</div>
              <div className="font-heading font-extrabold text-[#0A0D14] leading-none tracking-tight">Expofab</div>
            </div>
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8CA3A7] mt-3">Fabric Indenting Ops</p>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#1C2331] text-white"
                    : "text-[#4B5563] hover:bg-slate-50 hover:text-[#1C2331]"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="flex-1">{n.label}</span>
              {n.badge && reminderCount > 0 && (
                <span data-testid="reminder-badge" className="text-[10px] font-mono bg-[#D95D39] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                  {reminderCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-[#E6E4DD]">
          <div className="px-3 mb-3">
            <div className="text-xs text-[#8CA3A7] uppercase tracking-wider">Signed in</div>
            <div className="text-sm text-[#1C2331] font-medium truncate" data-testid="current-user-email">
              {user?.email}
            </div>
            <div className="text-[10px] uppercase tracking-wider mt-1 inline-block px-1.5 py-0.5 rounded-sm bg-slate-100 text-[#4B5563]" data-testid="current-user-role">{role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-[#991B1B] hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        <div className="max-w-6xl px-8 py-8 fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
